import type { Aspect, Design, Widget, WidgetType } from './types';
import { ASPECTS } from './types';
import { createWidget } from './widgets';
import { DEFAULT_GAP, DEFAULT_MARGIN, containShape, gridMetrics, lockedRatio, regrid } from './grid';

/**
 * Starting layouts. The essentials (salah times, clock) get the most space; smaller things such as the Donation QR
 * or Ayah of the Day get smaller cells. Each template is written on a coarse landscape (12 × 8) and portrait (6 × 10)
 * grid, then mapped exactly onto the screen's fixed grid; ultrawide and 4:3 screens use the landscape version.
 */

type Cell = [WidgetType, number, number, number, number, Record<string, unknown>?];

export interface Template {
  id: string;
  name: string;
  description: string;
  landscape: Cell[];
  portrait: Cell[];
}

export const TEMPLATES: Template[] = [
  {
    id: 'classic', name: 'Classic', description: 'Clock, full prayer times, Jumu‘ah, ayah and giving.',
    landscape: [['clock', 0, 0, 4, 4], ['prayerTimes', 4, 0, 8, 4], ['jumuah', 0, 4, 4, 3], ['ayah', 4, 4, 4, 3], ['donationQr', 8, 4, 4, 3], ['ticker', 0, 7, 12, 1]],
    portrait: [['clock', 0, 0, 6, 2], ['prayerTimes', 0, 2, 6, 3], ['jumuah', 0, 5, 3, 2], ['ayah', 3, 5, 3, 2], ['donationQr', 0, 7, 6, 2], ['ticker', 0, 9, 6, 1]],
  },
  {
    id: 'prayer-focus', name: 'Prayer Focus', description: 'Prayer times fill most of the screen, with a countdown to jama‘ah.',
    landscape: [['prayerTimes', 0, 0, 12, 5], ['clock', 0, 5, 7, 3], ['countdown', 7, 5, 5, 3]],
    portrait: [['prayerTimes', 0, 0, 6, 5, { layout: 'column' }], ['clock', 0, 5, 6, 2], ['countdown', 0, 7, 6, 3]],
  },
  {
    id: 'jumuah', name: 'Jumu‘ah', description: 'Khutbah and jama‘ah times up front for Fridays.',
    landscape: [['jumuah', 0, 0, 8, 4, { mode: 'times' }], ['clock', 8, 0, 4, 3], ['prayerTimes', 0, 4, 8, 3], ['countdown', 8, 3, 4, 4], ['ticker', 0, 7, 12, 1]],
    portrait: [['jumuah', 0, 0, 6, 4, { mode: 'times' }], ['prayerTimes', 0, 4, 6, 3], ['clock', 0, 7, 3, 2], ['countdown', 3, 7, 3, 2], ['ticker', 0, 9, 6, 1]],
  },
  {
    id: 'announcements', name: 'Announcements', description: 'A large notice board with prayer times below.',
    landscape: [['announcements', 0, 0, 12, 5], ['prayerTimes', 0, 5, 8, 3], ['clock', 8, 5, 4, 3]],
    portrait: [['announcements', 0, 0, 6, 5], ['prayerTimes', 0, 5, 6, 3], ['clock', 0, 8, 6, 2]],
  },
  {
    id: 'posters', name: 'Poster Showcase', description: 'A rotating poster with the clock and a prayer column.',
    landscape: [['posters', 0, 0, 6, 8], ['clock', 6, 0, 6, 2], ['prayerTimes', 6, 2, 6, 6, { layout: 'column', title: 'Today' }]],
    portrait: [['posters', 0, 0, 6, 7], ['prayerTimes', 0, 7, 6, 2], ['ticker', 0, 9, 6, 1]],
  },
  {
    id: 'split', name: 'Split Times', description: 'Prayer times as a column beside a big clock and notices.',
    landscape: [['prayerTimes', 0, 0, 4, 8, { layout: 'column' }], ['clock', 4, 0, 8, 4], ['announcements', 4, 4, 8, 3], ['ticker', 4, 7, 8, 1]],
    portrait: [['prayerTimes', 0, 0, 6, 4], ['clock', 0, 4, 6, 2], ['announcements', 0, 6, 6, 3], ['ticker', 0, 9, 6, 1]],
  },
  {
    id: 'giving', name: 'Giving', description: 'An appeal with progress and a donation QR code.',
    landscape: [['prayerTimes', 0, 0, 12, 3], ['donations', 0, 3, 7, 5], ['donationQr', 7, 3, 5, 5]],
    portrait: [['prayerTimes', 0, 0, 6, 3], ['donations', 0, 3, 6, 4], ['donationQr', 0, 7, 6, 3]],
  },
  {
    id: 'countdown', name: 'Countdown', description: 'A big countdown to the next jama‘ah, ideal for Ramadan.',
    landscape: [['countdown', 0, 0, 6, 4], ['clock', 6, 0, 6, 4], ['prayerTimes', 0, 4, 12, 3], ['ticker', 0, 7, 12, 1]],
    portrait: [['countdown', 0, 0, 6, 3], ['clock', 0, 3, 6, 2], ['prayerTimes', 0, 5, 6, 4], ['ticker', 0, 9, 6, 1]],
  },
  {
    id: 'knowledge', name: 'Knowledge', description: 'Ayah and hadith of the day above the prayer times.',
    landscape: [['ayah', 0, 0, 6, 4], ['hadith', 6, 0, 6, 4], ['prayerTimes', 0, 4, 12, 4]],
    portrait: [['ayah', 0, 0, 6, 3], ['hadith', 0, 3, 6, 3], ['prayerTimes', 0, 6, 6, 4]],
  },
  {
    id: 'community', name: 'Community Board', description: 'Events and notices for the week, with prayer times.',
    landscape: [['events', 0, 0, 5, 5], ['announcements', 5, 0, 7, 5], ['prayerTimes', 0, 5, 12, 3]],
    portrait: [['events', 0, 0, 6, 4], ['announcements', 0, 4, 6, 3], ['prayerTimes', 0, 7, 6, 3]],
  },
  {
    id: 'minimal', name: 'Minimal', description: 'Just a large clock and the day’s prayer times.',
    landscape: [['clock', 0, 0, 12, 5], ['prayerTimes', 0, 5, 12, 3]],
    portrait: [['clock', 0, 0, 6, 5], ['prayerTimes', 0, 5, 6, 5, { layout: 'column' }]],
  },
  {
    id: 'corridor', name: 'Corridor', description: 'Everything at a glance for screens people walk past.',
    landscape: [['clock', 0, 0, 4, 3], ['countdown', 4, 0, 4, 3], ['jumuah', 8, 0, 4, 3], ['prayerTimes', 0, 3, 12, 3], ['events', 0, 6, 8, 2], ['donationQr', 8, 6, 4, 2]],
    portrait: [['clock', 0, 0, 3, 2], ['countdown', 3, 0, 3, 2], ['prayerTimes', 0, 2, 6, 4], ['jumuah', 0, 6, 3, 2], ['donationQr', 3, 6, 3, 2], ['events', 0, 8, 6, 2]],
  },
  {
    id: 'ramadan', name: 'Ramadan', description: 'Iftar and Sehri up front, with Tarawih and the day’s times.',
    landscape: [['iftar', 0, 0, 4, 3], ['sehri', 4, 0, 4, 3], ['clock', 8, 0, 4, 3], ['prayerTimes', 0, 3, 12, 3], ['tarawih', 0, 6, 4, 2], ['ayah', 4, 6, 8, 2]],
    portrait: [['iftar', 0, 0, 3, 2], ['sehri', 3, 0, 3, 2], ['clock', 0, 2, 6, 2], ['prayerTimes', 0, 4, 6, 4], ['tarawih', 0, 8, 3, 2], ['ayah', 3, 8, 3, 2]],
  },
  {
    id: 'hero-clock', name: 'Hero Clock', description: 'One big clock, with the prayer times in a band beneath it.',
    landscape: [['clock', 0, 0, 12, 5], ['prayerTimes', 0, 5, 9, 3], ['countdown', 9, 5, 3, 3]],
    portrait: [['clock', 0, 0, 6, 4], ['countdown', 0, 4, 6, 2], ['prayerTimes', 0, 6, 6, 4]],
  },
  {
    id: 'sidebar', name: 'Sidebar', description: 'Posters and notices in the middle, prayer times down the side.',
    landscape: [['prayerTimes', 8, 0, 4, 8, { layout: 'column' }], ['clock', 0, 0, 4, 3], ['jumuah', 4, 0, 4, 3], ['announcements', 0, 3, 8, 4], ['ticker', 0, 7, 8, 1]],
    portrait: [['clock', 0, 0, 3, 2], ['jumuah', 3, 0, 3, 2], ['announcements', 0, 2, 6, 3], ['prayerTimes', 0, 5, 6, 4], ['ticker', 0, 9, 6, 1]],
  },
  {
    id: 'twin', name: 'Twin Clocks', description: 'The time and the countdown side by side, both large.',
    landscape: [['clock', 0, 0, 6, 5], ['countdown', 6, 0, 6, 5], ['prayerTimes', 0, 5, 12, 3]],
    portrait: [['clock', 0, 0, 6, 3], ['countdown', 0, 3, 6, 3], ['prayerTimes', 0, 6, 6, 4]],
  },
  {
    id: 'board', name: 'Prayer Board', description: 'The classic masjid board: the clock above every salah’s start and jama‘ah time.',
    landscape: [['clock', 0, 0, 5, 4], ['countdown', 0, 4, 5, 3], ['prayerTimes', 5, 0, 7, 7, { design: 'board' }], ['ticker', 0, 7, 12, 1]],
    portrait: [['clock', 0, 0, 6, 2], ['prayerTimes', 0, 2, 6, 7, { design: 'board' }], ['ticker', 0, 9, 6, 1]],
  },
  {
    id: 'clock-only', name: 'Clock', description: 'Just the time, the date and the Hijri date, as big as the screen allows.',
    landscape: [['clock', 0, 0, 12, 8, { showSeconds: true }]],
    portrait: [['clock', 0, 0, 6, 10, { showSeconds: true }]],
  },
  {
    id: 'notice-board', name: 'Notice Board', description: 'Notices, events and a hadith, with the times along the top.',
    landscape: [['prayerTimes', 0, 0, 12, 2], ['announcements', 0, 2, 6, 5], ['events', 6, 2, 3, 5], ['hadith', 9, 2, 3, 5], ['ticker', 0, 7, 12, 1]],
    portrait: [['prayerTimes', 0, 0, 6, 3], ['announcements', 0, 3, 6, 3], ['events', 0, 6, 3, 3], ['hadith', 3, 6, 3, 3], ['ticker', 0, 9, 6, 1]],
  },
];

/**
 * Layout collections: the same layouts dressed in a particular way, so a masjid can start from, say, borderless
 * widgets or analog faces throughout. A look sets each widget's style, border, corners and design; the screen's
 * background and the masjid's content are left alone.
 */
type Look = Partial<Pick<Widget, 'style' | 'border' | 'radius' | 'opacity'>> & { design?: string };
export interface LayoutCollection {
  id: string;
  name: string;
  description: string;
  /** The look of one widget. `i` is the layout's position in the list, so designs vary from layout to layout. */
  look: (type: WidgetType, i: number, n: number) => Look;
}

const pick = <T,>(xs: readonly T[], i: number) => xs[((i % xs.length) + xs.length) % xs.length]!;
const OUTLINE = '#8E9BB0';

export const LAYOUT_COLLECTIONS: LayoutCollection[] = [
  {
    id: 'standard', name: 'Standard', description: 'NoorView’s frosted glass panels, each with a fine edge.',
    look: () => ({}),
  },
  {
    id: 'bordered', name: 'With borders', description: 'Clear panels outlined with a crisp border.',
    look: (t, i) => ({ style: 'none', border: { width: pick([2, 3, 4], i), color: OUTLINE }, radius: pick([12, 20, 28, 6], i) }),
  },
  {
    id: 'borderless', name: 'Borderless', description: 'No panels at all: text and clocks sit straight on the background.',
    look: (t, i) => ({ style: 'none', border: null, design: t === 'prayerTimes' ? pick(['icons', 'table', 'tiles'], i) : undefined }),
  },
  {
    id: 'mixed', name: 'Mixed', description: 'Clocks float free while times and notices sit on panels.',
    look: (t, i, n) => {
      if (t === 'clock' || t === 'countdown' || t === 'logo') return { style: 'none', border: null };
      if (t === 'ticker') return { style: 'flat' };
      return { style: pick(['glass', '3d', 'flat'], i + n) };
    },
  },
  {
    id: 'digital', name: 'Digital only', description: 'Digital clocks, flip countdowns and numeric times.',
    look: (t, i) => {
      const d: Partial<Record<WidgetType, readonly string[]>> = {
        clock: ['digital', 'flip', 'stacked'], countdown: ['flip', 'digital'], prayerTimes: ['tiles', 'table', 'icons'],
        donations: ['numbers', 'bar'], jumuah: ['focus', 'times'], events: ['list', 'cards'],
      };
      return { design: d[t] ? pick(d[t]!, i) : undefined };
    },
  },
  {
    id: 'analog', name: 'Analog only', description: 'Clock faces throughout, with a dial for every salah.',
    look: (t, i) => {
      const d: Partial<Record<WidgetType, readonly string[]>> = {
        clock: ['analog', 'analog-minimal', 'analog-arabic'], countdown: ['ring', 'hourglass'], prayerTimes: ['clocks'],
        donations: ['ring', 'cards'], jumuah: ['focus'],
      };
      return { design: d[t] ? pick(d[t]!, i) : undefined };
    },
  },
  {
    id: 'glass', name: 'Glass', description: 'Frosted glass with soft, generous corners.',
    look: (t, i) => ({ style: 'glass', radius: pick([32, 40, 28], i), opacity: pick([100, 85], i) }),
  },
  {
    id: '3d', name: '3D', description: 'Raised panels with depth and a gentle shadow.',
    look: (t, i) => ({ style: '3d', radius: pick([18, 24, 14], i) }),
  },
  {
    id: 'flat', name: 'Flat', description: 'Solid, simple panels with no shine or shadow.',
    look: (t, i) => ({ style: 'flat', radius: pick([8, 12, 4, 0], i) }),
  },
];

/** A layout in a collection: the layout's id alone for Standard, otherwise `collection:layout`. */
export const collectionTemplateId = (c: string, t: string) => (c === 'standard' ? t : `${c}:${t}`);

function dress(widgets: Widget[], c: LayoutCollection, i: number): Widget[] {
  return widgets.map((w) => {
    const l = c.look(w.type, i, widgets.indexOf(w));
    const out: Widget = { ...w, props: { ...w.props } };
    if (l.style && w.type !== 'posters' && w.type !== 'logo') out.style = l.style;
    if (l.border !== undefined && w.type !== 'posters') out.border = l.border;
    if (l.radius !== undefined) out.radius = l.radius;
    if (l.opacity !== undefined) out.opacity = l.opacity;
    if (l.design) out.props.design = l.design;
    return out;
  });
}

export function blankDesign(aspect: Aspect = '16:9'): Design {
  const a = ASPECTS[aspect];
  return { schema: 1, aspect, cols: a.cols, rows: a.rows, gap: DEFAULT_GAP, margin: DEFAULT_MARGIN, background: { kind: 'gradient', color: '#0B1626', color2: '#02060D' }, widgets: [] };
}

/** Builds a template's widgets for the design's current aspect and grid. */
export function templateWidgets(t: Template, design: Pick<Design, 'aspect' | 'cols' | 'rows'> & Partial<Pick<Design, 'gap' | 'margin'>>, collection = 'standard'): Widget[] {
  const portrait = design.aspect === '9:16';
  const cells = portrait ? t.portrait : t.landscape;
  const base = portrait ? { cols: 6, rows: 10 } : { cols: 12, rows: 8 };
  const widgets = cells.map(([type, x, y, w, h, props]) => {
    const wd = createWidget(type, { x, y, w, h });
    if (props) wd.props = { ...wd.props, ...props };
    return wd;
  });
  const mapped = base.cols === design.cols && base.rows === design.rows ? widgets : regrid(widgets, base, design);
  const placed = fitPosters(mapped, { gap: DEFAULT_GAP, margin: DEFAULT_MARGIN, ...design });
  const c = LAYOUT_COLLECTIONS.find((x) => x.id === collection);
  return c && c.id !== 'standard' ? dress(placed, c, TEMPLATES.indexOf(t)) : placed;
}

/** Gives template posters their real shape, and lets the widgets beside them take up any space that frees. */
function fitPosters(widgets: Widget[], design: Pick<Design, 'aspect' | 'cols' | 'rows' | 'gap' | 'margin'>): Widget[] {
  const m = gridMetrics(design);
  const out = widgets.map((w) => ({ ...w }));
  for (const p of out) {
    const ratio = lockedRatio(p);
    if (!ratio) continue;
    const box = { x: p.x, y: p.y, w: p.w, h: p.h };
    const r = containShape(box, ratio, m);
    const fullWidth = box.x === 0 && box.w === design.cols;
    if (fullWidth) { Object.assign(p, r); continue; }
    Object.assign(p, { ...r, x: box.x, y: box.y });
    const freed = box.w - r.w;
    if (freed > 0) for (const n of out) if (n !== p && n.x === box.x + box.w) { n.x -= freed; n.w += freed; }
  }
  return out;
}

export function applyTemplate(design: Design, t: Template, collection = 'standard'): Design {
  return { ...design, widgets: templateWidgets(t, design, collection) };
}
